import { useCallback, useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Lock } from 'lucide-react'
import { ActionIcon, Badge, Group, Skeleton, Table, Text, Tooltip } from '@mantine/core'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import {
  listExtractionRuns,
  type ExtractionRunDTO,
  type ExtractionRunStatus,
} from '@/services/extractionRunService'
import { useAdminScope } from '../-admin/adminScope'

export const Route = createFileRoute('/_app/organization/admin/runs')({
  head: () => ({ meta: [{ title: 'Runs · Admin · AERRIS' }] }),
  component: RunsTab,
})

const PAGE_SIZE = 50

const STATUS_COLOR: Record<ExtractionRunStatus, string> = {
  COMPLETED: 'green',
  FAILED: 'red',
  RUNNING: 'blue',
  QUEUED: 'gray',
  SKIPPED: 'gray',
  SUPERSEDED: 'gray',
}

function RunsTab() {
  const { clientId, canManage, globalMode, scopeName, loading: scopeLoading } = useAdminScope()

  const [runs, setRuns] = useState<ExtractionRunDTO[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [runsLoading, setRunsLoading] = useState(true)

  const load = useCallback(async () => {
    if (scopeLoading) return
    if (globalMode || !clientId || !canManage) {
      setRuns([])
      setTotal(0)
      // There is nothing to fetch, but the skeleton is still up: without this the "select a
      // client" / "you need admin access" notices below are unreachable and the tab spins forever.
      setRunsLoading(false)
      return
    }
    setRunsLoading(true)
    try {
      const res = await listExtractionRuns(clientId, page, PAGE_SIZE)
      setRuns(res.content)
      setTotal(res.total)
    } catch (e) {
      console.error('Failed to load extraction runs:', e)
    } finally {
      setRunsLoading(false)
    }
  }, [clientId, canManage, globalMode, page, scopeLoading])

  useEffect(() => {
    void load()
  }, [load])

  const loading = scopeLoading || runsLoading

  if (globalMode) {
    return (
      <div className="notice">
        Extraction runs are recorded per client. Switch off “Global default” to view a client’s runs.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="taxonomy-skeleton" aria-busy="true">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} height={34} radius="sm" />
        ))}
      </div>
    )
  }

  if (!canManage) {
    return (
      <div className="notice">
        <Lock size={16} />
        {!clientId
          ? 'Select a client to view its extraction runs.'
          : `You need admin access to ${scopeName} to view its extraction runs.`}
      </div>
    )
  }

  if (runs.length === 0) {
    return (
      <div className="taxonomy-empty">
        <h2 className="taxonomy-empty-title">No extraction runs yet</h2>
        <p className="taxonomy-empty-text">
          Runs appear here once contracts are processed or re-processed for {scopeName}. Each row records
          the taxonomy and prompt version that produced it.
        </p>
      </div>
    )
  }

  const from = page * PAGE_SIZE + 1
  const to = Math.min(page * PAGE_SIZE + runs.length, total)
  const hasPrev = page > 0
  const hasNext = page * PAGE_SIZE + runs.length < total

  return (
    <div className="taxonomy-page">
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Contract</Table.Th>
            <Table.Th>Project</Table.Th>
            <Table.Th style={{ width: 56 }}>Run</Table.Th>
            <Table.Th>Category</Table.Th>
            <Table.Th style={{ width: 110 }}>Status</Table.Th>
            <Table.Th>Model</Table.Th>
            <Table.Th>Versions</Table.Th>
            <Table.Th>By</Table.Th>
            <Table.Th>When</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {runs.map((r) => (
            <Table.Tr key={r.uuid}>
              <Table.Td>
                {r.contractUuid ? (
                  <Tooltip
                    label={r.contractName ? `${r.contractName} · ${r.contractUuid}` : r.contractUuid}
                    withArrow
                  >
                    <Link
                      to="/law/vault/analysis/$contractId"
                      params={{ contractId: r.contractUuid }}
                      style={{ color: 'var(--accent-ink)', fontWeight: 500 }}
                    >
                      {r.contractName ?? r.contractUuid.slice(0, 8)}
                    </Link>
                  </Tooltip>
                ) : (
                  <Text size="sm" c="dimmed">—</Text>
                )}
              </Table.Td>
              <Table.Td>{r.projectName ?? <Text size="sm" c="dimmed">—</Text>}</Table.Td>
              <Table.Td>{r.runNumber != null ? `#${r.runNumber}` : '—'}</Table.Td>
              <Table.Td>{r.category ?? <Text size="sm" c="dimmed">—</Text>}</Table.Td>
              <Table.Td>
                {r.status ? (
                  <Badge variant="light" color={STATUS_COLOR[r.status] ?? 'gray'} size="sm">
                    {r.status.toLowerCase()}
                  </Badge>
                ) : (
                  '—'
                )}
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">{r.model ?? '—'}</Text>
              </Table.Td>
              <Table.Td>
                <Group gap={6} wrap="nowrap">
                  {r.taxonomyVersion != null ? (
                    r.category ? (
                      <Link
                        to="/organization/admin/versions"
                        search={(prev) => ({ ...prev, sel: `tax:${r.category}` })}
                        style={{ color: 'var(--accent-ink)', fontWeight: 500, fontSize: 13 }}
                      >
                        tax v{r.taxonomyVersion}
                      </Link>
                    ) : (
                      <Text size="sm">tax v{r.taxonomyVersion}</Text>
                    )
                  ) : null}
                  {r.promptVersion != null && (
                    <Text size="sm" c="dimmed">prompt v{r.promptVersion}</Text>
                  )}
                  {r.taxonomyVersion == null && r.promptVersion == null && (
                    <Text size="sm" c="dimmed">—</Text>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">{r.triggeredBy ?? '—'}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {r.finishedAt || r.startedAt
                    ? new Date(r.finishedAt ?? (r.startedAt as string)).toLocaleString()
                    : '—'}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Group justify="flex-end" gap="sm" mt="md">
        <Text size="sm" c="dimmed">
          {from}–{to} of {total}
        </Text>
        <ActionIcon variant="default" disabled={!hasPrev} onClick={() => setPage((p) => Math.max(0, p - 1))} aria-label="Previous page">
          <ChevronLeft size={16} />
        </ActionIcon>
        <ActionIcon variant="default" disabled={!hasNext} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
          <ChevronRight size={16} />
        </ActionIcon>
      </Group>
    </div>
  )
}
